import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseComponentImpl } from '../types/implementations';
import {
    applyComponentFieldPatch,
    ComponentFieldPatch,
    unconfirmedPatchFields,
} from '../Helpers/componentPatch';

interface SavedPatch {
    componentKey: string;
    patch: ComponentFieldPatch;
}

/**
 * The component as it was passed in, with the edits saved from this view laid
 * over it until the passed-in component shows them. A view therefore shows its
 * own saves whether or not its host re-derives the component after an edit.
 */
export function usePatchedComponent(source: BaseComponentImpl): {
    component: BaseComponentImpl;
    recordPatch: (componentKey: string, patch: ComponentFieldPatch) => void;
} {
    const [saved, setSaved] = useState<SavedPatch>({ componentKey: source.key, patch: {} });
    const sourceKeyRef = useRef(source.key);
    sourceKeyRef.current = source.key;

    const pending = useMemo(
        () => (saved.componentKey === source.key ? unconfirmedPatchFields(source, saved.patch) : {}),
        [saved, source]
    );

    // Forget the fields the passed-in component has caught up with, so a later
    // change to them from outside is not hidden behind an old save
    useEffect(() => {
        setSaved(prev => {
            if (prev.componentKey !== source.key) return { componentKey: source.key, patch: {} };
            const remaining = unconfirmedPatchFields(source, prev.patch);
            return Object.keys(remaining).length === Object.keys(prev.patch).length
                ? prev
                : { componentKey: source.key, patch: remaining };
        });
    }, [source]);

    const component = useMemo(
        () => (Object.keys(pending).length === 0
            ? source
            : new BaseComponentImpl(applyComponentFieldPatch(source, pending))),
        [source, pending]
    );

    // A save that finishes after the view has moved on to another component is
    // not laid over the new one
    const recordPatch = useCallback((componentKey: string, patch: ComponentFieldPatch) => {
        if (componentKey !== sourceKeyRef.current) return;
        setSaved(prev => ({
            componentKey,
            patch: prev.componentKey === componentKey ? { ...prev.patch, ...patch } : { ...patch },
        }));
    }, []);

    return { component, recordPatch };
}
